import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box, Checkbox, Select, MenuItem, TextField, IconButton, Button, Typography, Divider,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import type { UIField } from '../types/applicationSettings';
import type { EmployeeRecord } from '../types/employees';

// ─── Types ───────────────────────────────────────────────────────────────────

export type JoinOp = 'AND' | 'OR';

export interface FilterCondition {
  id: string;
  fieldName: string;
  operator: string;
  value: string;
  enabled: boolean;
}

export interface FilterGroup {
  id: string;
  join: JoinOp;   // how this group joins with the previous group (ignored for first)
  logic: JoinOp;  // AND = all conditions match, OR = any condition matches
  conditions: FilterCondition[];
}

export interface EmployeeFilterProps {
  uiFields: UIField[];
  employees: EmployeeRecord[];
  onChange?: (uuids: string[] | null) => void;
  onSearch?: () => void;
  searchDisabled?: boolean;
}

// ─── Operator definitions ─────────────────────────────────────────────────────

interface OpDef { symbol: string; label: string; }

const OP_DEFS: Record<string, OpDef> = {
  contains:     { symbol: '~',  label: 'Contains' },
  not_contains: { symbol: '!~', label: 'Not contains' },
  equals:       { symbol: '=',  label: 'Equals' },
  not_equals:   { symbol: '≠',  label: 'Not equals' },
  starts_with:  { symbol: '^',  label: 'Starts with' },
  ends_with:    { symbol: '$',  label: 'Ends with' },
  has_value:    { symbol: '∃',  label: 'Has value' },
  is_empty:     { symbol: '∅',  label: 'Is empty' },
  gt:           { symbol: '>',  label: '>' },
  gte:          { symbol: '≥',  label: '≥' },
  lt:           { symbol: '<',  label: '<' },
  lte:          { symbol: '≤',  label: '≤' },
  before:       { symbol: '<',  label: '<' },
  after:        { symbol: '>',  label: '>' },
};

const TEXT_OPS  = ['contains', 'not_contains', 'equals', 'not_equals', 'starts_with', 'ends_with', 'has_value', 'is_empty'];
const COMBO_OPS = ['contains', 'not_contains', 'equals', 'not_equals', 'has_value', 'is_empty'];
const DATE_OPS  = ['equals', 'before', 'after', 'has_value', 'is_empty'];
const NO_VALUE_OPS = new Set(['has_value', 'is_empty']);

function opsForField(field: UIField | undefined): string[] {
  if (!field) return TEXT_OPS;
  if (field.type === 'tag') return COMBO_OPS;
  switch (field.inputType) {
    case 'COMBO_BOX':   return COMBO_OPS;
    case 'DATE_PICKER': return DATE_OPS;
    default:            return TEXT_OPS;
  }
}

function defaultOp(field: UIField | undefined): string {
  if (!field) return 'contains';
  if (field.type === 'tag') return 'equals';
  switch (field.inputType) {
    case 'COMBO_BOX':   return 'equals';
    case 'DATE_PICKER': return 'equals';
    default:            return 'contains';
  }
}

// ─── Value extraction ─────────────────────────────────────────────────────────

function extractValue(record: EmployeeRecord, fieldName: string): string[] | string {
  const v = record[fieldName];
  if (v == null) return '';

  if (Array.isArray(v)) {
    if (v.length === 0) return '';
    // Check if it's a history structure inside the array
    if (v[0] != null && typeof v[0] === 'object' && 'value' in v[0]) {
      const latestValue = (v[v.length - 1] as any)?.value;
      if (Array.isArray(latestValue)) return latestValue.map(String);
      return latestValue != null ? String(latestValue) : '';
    }
    // Basic multi-select array
    return v.map(String);
  }
  return String(v);
}

// ─── Match function ───────────────────────────────────────────────────────────

function matchRecord(
  record: EmployeeRecord,
  cond: FilterCondition,
  uiFieldMap: Map<string, UIField>,
): boolean {
  const raw = extractValue(record, cond.fieldName);
  const field = uiFieldMap.get(cond.fieldName);

  const isArr = Array.isArray(raw);
  const rawStr = isArr ? (raw as string[]).join(',') : (raw as string);

  if (cond.operator === 'has_value') return rawStr.trim() !== '';
  if (cond.operator === 'is_empty')  return rawStr.trim() === '';

  if (field?.inputType === 'DATE_PICKER') {
    if (!cond.value) return false;
    const empDate = new Date(rawStr).setHours(0, 0, 0, 0);
    const cmpDate = new Date(cond.value).setHours(0, 0, 0, 0);
    if (cond.operator === 'equals') return empDate === cmpDate;
    if (cond.operator === 'before') return empDate < cmpDate;
    if (cond.operator === 'after')  return empDate > cmpDate;
    return false;
  }

  const n = cond.value.toLowerCase();

  // If the raw data is an array (multi-select), strictly search the collection
  if (isArr) {
    const arr = (raw as string[]).map((s) => s.toLowerCase());
    switch (cond.operator) {
      case 'equals':
      case 'contains':
        return arr.includes(n);
      case 'not_equals':
      case 'not_contains':
        return !arr.includes(n);
    }
    return false;
  }

  // Regular string processing
  const h = (raw as string).toLowerCase();
  switch (cond.operator) {
    case 'equals':      return h === n;
    case 'not_equals':  return h !== n;
    case 'contains':    return h.includes(n);
    case 'not_contains':return !h.includes(n);
    case 'starts_with': return h.startsWith(n);
    case 'ends_with':   return h.endsWith(n);
  }
  return true;
}

// ─── Factories ────────────────────────────────────────────────────────────────

function makeCondition(uiFields: UIField[]): FilterCondition {
  const first = uiFields[0];
  return { id: Date.now().toString(), fieldName: first?.name ?? '', operator: defaultOp(first), value: '', enabled: true };
}

function makeGroup(uiFields: UIField[], join: JoinOp = 'OR'): FilterGroup {
  return { id: Date.now().toString(), join, logic: 'AND', conditions: [makeCondition(uiFields)] };
}

// ─── Component ───────────────────────────────────────────────────────────────

const EmployeeFilter: React.FC<EmployeeFilterProps> = ({ uiFields, employees, onChange, onSearch, searchDisabled }) => {
  const activeUiFields = useMemo(() => uiFields.filter((f) => f.active), [uiFields]);
  const [groups, setGroups] = useState<FilterGroup[]>(() => [makeGroup(activeUiFields, 'OR')]);

  const uiFieldMap = useMemo(
    () => new Map(activeUiFields.map((f) => [f.name, f])),
    [activeUiFields],
  );

  // ── Compute filtered UUIDs ──────────────────────────────────────────────────
  const computed = useMemo(() => {
    // Evaluate each non-empty group
    const activeGroups: Array<{ join: JoinOp; matches: boolean[] }> = [];
    for (const group of groups) {
      const active = group.conditions.filter(
        (c) => c.enabled && (NO_VALUE_OPS.has(c.operator) || c.value.trim() !== ''),
      );
      if (active.length === 0) continue;
      const matches = employees.map((emp) =>
        group.logic === 'AND'
          ? active.every((c) => matchRecord(emp, c, uiFieldMap))
          : active.some((c) => matchRecord(emp, c, uiFieldMap)),
      );
      activeGroups.push({ join: group.join, matches });
    }

    if (activeGroups.length === 0) return null;

    return employees
      .filter((_, i) => {
        let result = activeGroups[0].matches[i];
        for (let g = 1; g < activeGroups.length; g++) {
          const m = activeGroups[g].matches[i];
          result = activeGroups[g].join === 'AND' ? result && m : result || m;
        }
        return result;
      })
      .map((e) => String(e['employeeUUID'] ?? ''));
  }, [groups, employees, uiFieldMap]);

  useEffect(() => {
    onChange?.(computed);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computed]);

  // ── Group handlers ──────────────────────────────────────────────────────────
  const addGroup = useCallback(() => {
    setGroups((prev) => [...prev, { ...makeGroup(activeUiFields), id: Date.now().toString() }]);
  }, [activeUiFields]);

  const removeGroup = useCallback((gid: string) => {
    setGroups((prev) => {
      if (prev.length === 1) return [makeGroup(activeUiFields, 'OR')];
      return prev.filter((g) => g.id !== gid);
    });
  }, [activeUiFields]);

  const updateGroupJoin = useCallback((gid: string, join: JoinOp) => {
    setGroups((prev) => prev.map((g) => (g.id === gid ? { ...g, join } : g)));
  }, []);

  const updateGroupLogic = useCallback((gid: string, logic: JoinOp) => {
    setGroups((prev) => prev.map((g) => (g.id === gid ? { ...g, logic } : g)));
  }, []);

  // ── Condition handlers ──────────────────────────────────────────────────────
  const addCondition = useCallback((gid: string) => {
    setGroups((prev) =>
      prev.map((g) =>
        g.id === gid
          ? { ...g, conditions: [...g.conditions, { ...makeCondition(activeUiFields), id: Date.now().toString() }] }
          : g,
      ),
    );
  }, [activeUiFields]);

  const removeCondition = useCallback((gid: string, cid: string) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== gid) return g;
        if (g.conditions.length === 1) return { ...g, conditions: [makeCondition(activeUiFields)] };
        return { ...g, conditions: g.conditions.filter((c) => c.id !== cid) };
      }),
    );
  }, [activeUiFields]);

  const updateField = useCallback((gid: string, cid: string, fieldName: string) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== gid) return g;
        return {
          ...g,
          conditions: g.conditions.map((c) => {
            if (c.id !== cid) return c;
            const field = uiFieldMap.get(fieldName);
            return { ...c, fieldName, operator: defaultOp(field), value: '' };
          }),
        };
      }),
    );
  }, [uiFieldMap]);

  const updateOperator = useCallback((gid: string, cid: string, operator: string) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== gid) return g;
        return {
          ...g,
          conditions: g.conditions.map((c) =>
            c.id === cid
              ? { ...c, operator, value: NO_VALUE_OPS.has(operator) ? '' : c.value }
              : c,
          ),
        };
      }),
    );
  }, []);

  const updateValue = useCallback((gid: string, cid: string, value: string) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== gid) return g;
        return { ...g, conditions: g.conditions.map((c) => (c.id === cid ? { ...c, value } : c)) };
      }),
    );
  }, []);

  const toggleConditionEnabled = useCallback((gid: string, cid: string) => {
    setGroups((prev) =>
      prev.map((g) => {
        if (g.id !== gid) return g;
        return { ...g, conditions: g.conditions.map((c) => (c.id === cid ? { ...c, enabled: !c.enabled } : c)) };
      }),
    );
  }, []);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
      {groups.map((group, gIdx) => (
        <React.Fragment key={group.id}>
          {/* Inter-group join connector */}
          {gIdx > 0 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, my: 0.25 }}>
              <Divider sx={{ flex: 1 }} />
              <Select
                value={group.join}
                onChange={(e: SelectChangeEvent) => updateGroupJoin(group.id, e.target.value as JoinOp)}
                variant="outlined"
                size="small"
                sx={{
                  fontSize: '0.7rem', fontWeight: 700, color: 'primary.main', minWidth: 0,
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-select': { padding: '0 20px 0 2px !important', minHeight: 'unset !important' },
                  '& .MuiSelect-icon': { right: 0 },
                }}
              >
                <MenuItem value="AND" sx={{ fontSize: '0.72rem' }}>AND</MenuItem>
                <MenuItem value="OR"  sx={{ fontSize: '0.72rem' }}>OR</MenuItem>
              </Select>
              <Divider sx={{ flex: 1 }} />
            </Box>
          )}

          {/* Group box */}
          <Box sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}>
            {/* Group header */}
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 0.5,
              px: 1, py: 0.4,
              bgcolor: 'action.hover',
              borderBottom: '1px solid', borderColor: 'divider',
            }}>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', flexShrink: 0, lineHeight: 1 }}>
                Match
              </Typography>
              <Select
                value={group.logic}
                onChange={(e: SelectChangeEvent) => updateGroupLogic(group.id, e.target.value as JoinOp)}
                variant="outlined"
                size="small"
                renderValue={(v) => (v === 'AND' ? 'ALL' : 'ANY')}
                sx={{
                  fontSize: '0.7rem', fontWeight: 700, color: 'primary.main', minWidth: 0,
                  '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                  '& .MuiSelect-select': { padding: '0 20px 0 2px !important', minHeight: 'unset !important' },
                  '& .MuiSelect-icon': { right: 0 },
                }}
              >
                <MenuItem value="AND" sx={{ fontSize: '0.72rem' }}>ALL conditions</MenuItem>
                <MenuItem value="OR"  sx={{ fontSize: '0.72rem' }}>ANY condition</MenuItem>
              </Select>
              <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', flex: 1, lineHeight: 1 }}>
                of:
              </Typography>
              <IconButton
                size="small"
                onClick={() => removeGroup(group.id)}
                sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Box>

            {/* Conditions */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.4, p: 0.75 }}>
              {group.conditions.map((cond) => {
                const field = uiFieldMap.get(cond.fieldName);
                const ops = opsForField(field);
                const needsValue = !NO_VALUE_OPS.has(cond.operator);
                const isDate = field?.inputType === 'DATE_PICKER' && needsValue;
                const isCombo = (field?.inputType === 'COMBO_BOX' || field?.type === 'tag')
                  && needsValue
                  && ['equals', 'not_equals', 'contains', 'not_contains'].includes(cond.operator)
                  && field?.possibleValues
                  && Object.keys(field.possibleValues).length > 0;

                return (
                  <Box key={cond.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.4, opacity: cond.enabled ? 1 : 0.4 }}>
                    {/* Enable toggle */}
                    <Checkbox
                      checked={cond.enabled}
                      onChange={() => toggleConditionEnabled(group.id, cond.id)}
                      size="small"
                      sx={{ p: 0.25, flexShrink: 0 }}
                    />
                    {/* Field */}
                    <Select
                      value={cond.fieldName}
                      onChange={(e: SelectChangeEvent) => updateField(group.id, cond.id, e.target.value)}
                      size="small"
                      MenuProps={{ PaperProps: { sx: { maxHeight: 8 * 36 } } }}
                      sx={{ fontSize: '0.72rem', width: 120, flexShrink: 0 }}
                    >
                      {activeUiFields.map((f) => (
                        <MenuItem key={f.name} value={f.name} sx={{ fontSize: '0.72rem' }}>
                          {f.title}
                        </MenuItem>
                      ))}
                    </Select>

                    {/* Operator */}
                    <Select
                      value={cond.operator}
                      onChange={(e: SelectChangeEvent) => updateOperator(group.id, cond.id, e.target.value)}
                      size="small"
                      sx={{ fontSize: '0.72rem', width: 120, flexShrink: 0 }}
                    >
                      {ops.map((op) => (
                        <MenuItem key={op} value={op} sx={{ fontSize: '0.72rem' }}>
                          {OP_DEFS[op]?.label ?? op}
                        </MenuItem>
                      ))}
                    </Select>

                    {/* Value */}
                    {needsValue && (
                      isCombo ? (
                        <Select
                          value={cond.value}
                          onChange={(e: SelectChangeEvent) => updateValue(group.id, cond.id, e.target.value)}
                          size="small"
                          displayEmpty
                          sx={{ flex: 1, minWidth: 0, fontSize: '0.72rem' }}
                        >
                          <MenuItem value="" sx={{ fontSize: '0.72rem', color: 'text.disabled' }}>— any —</MenuItem>
                          {Object.entries(field!.possibleValues!).map(([k, v]) => (
                            <MenuItem key={k} value={k} sx={{ fontSize: '0.72rem' }}>{v}</MenuItem>
                          ))}
                        </Select>
                      ) : (
                        <TextField
                          value={cond.value}
                          onChange={(e) => updateValue(group.id, cond.id, e.target.value)}
                          type={isDate ? 'date' : 'text'}
                          size="small"
                          sx={{ flex: 1, minWidth: 0, '& .MuiInputBase-input': { fontSize: '0.72rem' } }}
                        />
                      )
                    )}

                    {/* Delete condition */}
                    <IconButton
                      size="small"
                      onClick={() => removeCondition(group.id, cond.id)}
                      sx={{ p: 0.25, flexShrink: 0, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                    >
                      <CloseIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                );
              })}

              {/* Add condition */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  size="small"
                  startIcon={<AddIcon sx={{ fontSize: '0.78rem !important' }} />}
                  onClick={() => addCondition(group.id)}
                  sx={{ fontSize: '0.68rem', textTransform: 'none', py: 0.2 }}
                >
                  Add condition
                </Button>
              </Box>
            </Box>
          </Box>
        </React.Fragment>
      ))}

      {/* Add group row — Search lives here when provided */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {onSearch ? (
          <Button
            variant="contained"
            size="small"
            disabled={searchDisabled}
            onClick={onSearch}
          >
            Search
          </Button>
        ) : <Box />}
        <Button
          size="small"
          startIcon={<AddIcon sx={{ fontSize: '0.8rem !important' }} />}
          onClick={addGroup}
          sx={{ fontSize: '0.7rem', textTransform: 'none', py: 0.25 }}
        >
          Add group
        </Button>
      </Box>
    </Box>
  );
};

export default EmployeeFilter;
